import type { SVGProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * Storefront burger / menu icon — three horizontal pill bars in a justified
 * stair-step layout (medium / long / short), inspired by the Pinterest design
 * reference. Hand-built SVG, not Phosphor.
 *
 * Direction: the bars are drawn right-edge aligned (natural for RTL / Arabic).
 * In LTR documents the SVG is mirrored horizontally via `ltr:-scale-x-100`
 * (Tailwind's `ltr:` variant targets `[dir=ltr]` ancestors), so the bars end
 * up left-edge aligned — reading direction is preserved in both locales.
 *
 * Sizing: pass `size` (px) explicitly, OR let the parent control via
 * Tailwind's `[&_svg]:h-X [&_svg]:w-X` rules (the way `IconButton` does it).
 *
 * Color: uses `currentColor` for the stroke, so it follows the parent's text
 * color (matches Phosphor's behavior).
 */
export function MenuJustifiedIcon({
  size,
  className,
  ...props
}: SVGProps<SVGSVGElement> & { size?: number | string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      className={cn('ltr:-scale-x-100', className)}
      {...props}
    >
      {/* Top bar — medium length, sits right of center (mirrored in LTR) */}
      <line x1="9" y1="6" x2="20" y2="6" />
      {/* Middle bar — longest, extends furthest left (right in LTR after mirror) */}
      <line x1="4" y1="12" x2="20" y2="12" />
      {/* Bottom bar — shortest, anchored to the right (left in LTR after mirror) */}
      <line x1="13" y1="18" x2="20" y2="18" />
    </svg>
  );
}
